import Papa from 'papaparse';
import api from './api'

/**
 * @function downloadCSV
 * @description downloads the csv file
 * @param {sd} data - the type of data
 * @param {string} filename - the name of exported csv file
 */
export const downloadCSV = (data, filename) => {
  const csvConfig = {
    quotes: [true, true],
    delimiter: ',',
    newline: '\n',
    header: true
  }

  const csvData = Papa.unparse(data, csvConfig);
  const blob = new Blob([csvData], {
    type: 'text/csv'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadCSVCustomColumns = (data, columns, filename) => {
  const csvConfig = {
    quotes: [true, true],
    delimiter: ',',
    newline: '\n',
    header: false,
    columns: data.fields
  }

  const csvData = Papa.unparse(data, csvConfig);
  const headersData = Papa.unparse({fields:columns, data:[]});
  const result = headersData+csvData
  const blob = new Blob([result], {
    type: 'text/csv'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getEnv = () => {
  return new Promise(resolve => {
    api.buildRequest('getEnv', 'GET').then(({ data }) => {
      // Assign baseURL if region is ap-south-1 else assign default baseURL
      data.baseURL = data.region === 'ap-south-1' ? `https://api-${data.region}.nivi.io` : process.env.BACKEND_API_ORIGIN
      resolve(data)
    })
  })
}

export const getDisplayMessage = (message, hideEmptyTranslations) => {
  if (!hideEmptyTranslations) return JSON.parse(JSON.stringify(message))
  else {
    let nonEmptyCountrySpecificLanguageDetails = {}
    for (let country in message) { // iteration over object key
      for (let lang in message[country]) { // iteration over object key
        if (message[country][lang].length > 0) {
          // Create new country index only if there is any data for any of the language
          if (!nonEmptyCountrySpecificLanguageDetails[country]) nonEmptyCountrySpecificLanguageDetails[country] = {}
          nonEmptyCountrySpecificLanguageDetails[country][lang] = JSON.parse(JSON.stringify(message[country][lang]))
        }
      }
    }
    return nonEmptyCountrySpecificLanguageDetails
  }
}

export const getSelectorValue = (selector) => {
  for (const [key, value] of Object.entries(selector)) {
    if (value && typeof value === 'string') selector[key] = value.replace(/'/g, "''")
  }
  return selector
}

/**
 * @function formatDate
 * @description formats the date coming from mongodb
 * @param {string} date - the date value stored in db
 */
export const formatDate = (date, format, convertUtc = false) => {
  if (!date) {
    return ''
  }
  var theDate = theDate = new Date(date)
  if (convertUtc) {
    let currentDateObj = new Date(date)
    var new_utc = new Date(currentDateObj.getTime() + currentDateObj.getTimezoneOffset() * 60000);
    theDate = new_utc
  } 
  let dateToReturn = theDate

  const dateToDisplay = ('0' + theDate.getDate()).slice(-2)
  const monthToDisplay = ('0' + (theDate.getMonth() + 1)).slice(-2)

  const hoursToDisplay = ('0' + theDate.getHours()).slice(-2)
  const minutesToDisplay = ('0' + theDate.getMinutes()).slice(-2)

  switch (format) {
    case 'd/M/Y hh:ss':
      dateToReturn = `${dateToDisplay}/${monthToDisplay}/${theDate.getFullYear()} ${hoursToDisplay}:${minutesToDisplay}`
      break
    case 'd/M/Y':
      dateToReturn = `${dateToDisplay}/${monthToDisplay}/${theDate.getFullYear()}`
      break
    default:
      dateToReturn = `${dateToDisplay}/${monthToDisplay}/${theDate.getFullYear()} ${hoursToDisplay}:${minutesToDisplay}`
  }
  return dateToReturn
}


/**
 * @function formatDate
 * @description get the delay in hours based on quantity and unit for that delay
 * @param {object} delay - {quantity: '23', units: 'days'}
 */
export const findDelayInHours = (delay) => {
  let time;
  if (delay.units === 'hours') {
    time = parseInt(delay.quantity)
  }
  else if (delay.units === 'days') {
    time = parseInt(delay.quantity) * 24
  }
  else if (delay.units = 'weeks') {
    time = parseInt(delay.quantity) * 24 * 7
  }
  return time
}


/**
 * @function validateAllLanguageTranslation
 * @description get the list of all missing translation stages
 * @param {object} stages - array of stages
 */
export const validateAllLanguageTranslation = (stages) => {
  let countryLangObj = {'Global': ['eng']}
  stages.forEach(stage => {
    if (stage.text && stage.text.length) {
      stage.text.forEach(message => {
        Object.entries(message).forEach(([country, langObj]) => {
          Object.keys(langObj).forEach(lang => {
            if (countryLangObj[country]) {
              if (!countryLangObj[country].includes(lang)) {
                countryLangObj[country].push(lang)
              }
            } else {
              countryLangObj[country] = [lang]
            }
          })
        })
      })
    }
    if (stage.validations && stage.validations.type === 'DynamicNumber') {
      stage.optionConfig.forEach(option => {
        if (option.dynamic || option.isNullList) return
        Object.entries(option.option_text).forEach(([country, langObj]) => {
          Object.keys(langObj).forEach(lang => {
            if (countryLangObj[country]) {
              if (!countryLangObj[country].includes(lang)) {
                countryLangObj[country].push(lang)
              }
            } else {
              countryLangObj[country] = [lang]
            }
          })
        })
      })
    }
  })

  let missingStages = []
  stages.forEach(stage => {
    if (stage.text && stage.text.length) {
      stage.text.forEach((message, index) => {
        let missingLanguages = []
        let globalEnglishPresent = false
        // Check if everything is present or not
        Object.entries(countryLangObj).forEach(([country, languages]) => {
          if (country === 'Global' && languages.includes('eng')) {
            globalEnglishPresent = true
          }
          if (!(country in message)) {
            missingLanguages.push(...languages.map(item => `${item} (${country})`))
          } else {
            // check if message set is present for that country
            languages.forEach(lang => {
              if (!(lang in message[country])) {
                missingLanguages.push(`${lang} (${country})`)
              }
            })
          }
        })
        if (missingLanguages.includes('eng (Global)') || globalEnglishPresent) {
          missingLanguages = missingLanguages.filter(item => item === 'eng (Global)' || !item.includes('eng'))
        }
        if (missingLanguages.length) {
          missingStages.push({
            stage: stage.stageName,
            msgSet: `Message${index + 1}`,
            missingLang: missingLanguages
          })
        }
      })
    }

    if (stage.validations && stage.validations.type === 'DynamicNumber') {
      stage.optionConfig.forEach((option, optionIndex) => {
        // Check for dynamic option - If dynamic then no need to check translations
        if (option.dynamic || option.isNullList) return

        let missingLanguages = []
        let globalEnglishPresent = false
        // Check if everything is present or not
        Object.entries(countryLangObj).forEach(([country, languages]) => {
          if (country === 'Global' && languages.includes('eng')) {
            globalEnglishPresent = true
          }
          if (!(country in option.option_text)) {
            missingLanguages.push(...languages.map(item => `${item} (${country})`))
          } else {
            // check if message set is present for that country
            languages.forEach(lang => {
              if (!(lang in option.option_text[country])) {
                missingLanguages.push(`${lang} (${country})`)
              }
            })
          }
        })
        if (missingLanguages.includes('eng (Global)') || globalEnglishPresent) {
          missingLanguages = missingLanguages.filter(item => item === 'eng (Global)' || !item.includes('eng'))
        }
        if (missingLanguages.length) {
          missingStages.push({
            stage: stage.stageName,
            msgSet: `Dynamic Option ${optionIndex + 1}`,
            missingLang: missingLanguages
          })
        }
      })
    }
  })
  return missingStages
}

/*
  Sort stages based on delay is set for JOR stages
*/
export const sortStagesForJor = (convo) => {
  let stages = convo.config.stages
  let stageArray = Object.entries(stages).map(e => e[1])
  let jorStages = []
  let stageMap = new Map()
  let visited = []
  let nextStages = []

  // get the array of stages which are of type journeyReminder
  for (const item of stageArray) {
    if (item.stageType === 'journeyReminder') {
      // calculate totalDelay based on selected values
      item.totalDelay = findDelayInHours(item.joRDelayConfig.delay)
      jorStages.push(item)
    }
  }

  // Sort the array of JoR stages based on totalDelay
  jorStages.sort((a, b) => a.totalDelay - b.totalDelay)

  // Set JoR stages at the start and then set its next stage to generate the graph in sorted manner
  jorStages.forEach(item => {
    if (!visited.includes(item.stageName)) visited.push(item.stageName)
    if (item.next && !visited.includes(item.next)) {
      nextStages.push(item.next)
      while (nextStages.length) {
        if (visited.includes(nextStages[0])) {
          nextStages.shift()
          continue
        }
        if (!visited.includes(nextStages[0])) visited.push(nextStages[0])
        if (stages[nextStages[0]] && stages[nextStages[0]].next) {
          if (typeof stages[nextStages[0]].next === 'string') {
            nextStages.push(stages[nextStages[0]].next)
          } else if (typeof stages[nextStages[0]].next === 'object') {
            nextStages.push(...stages[nextStages[0]].next)
          }
        }
        nextStages.shift()
      }
    }
      })

  // Check for rest of the stages, if they are not present in finalStageObj then push them in the array
  for (let stage in stages) {
    if (!visited.includes(stage)) {
      visited.push(stage)
    }
  }

  visited.forEach(item => stageMap.set(item, stages[item]))

  // set the value of stages to the object and return it
  convo.config.stages = stageMap

  return convo
}

/**
 * @function getStagesInBFSOrder
 * @description get the list of all stages in BFS order
 * @param {object} stagesobj - object of stages
 */
export const getStagesInBFSOrder = (app, isJORConversation) => {
  let stagesobj = app.config.stages
  let visited = {}
  let queue = ['uninitialized']
  let finalArr = []
  let tempQueue = []

  Object.keys(stagesobj).forEach(stage => {
    visited[stage] = false
  })

  do {
    let currentStage = queue.shift()
    if (visited[currentStage] === false && stagesobj[currentStage].next) {
      let nextStages = [...new Set(Array.isArray(stagesobj[currentStage].next) ? stagesobj[currentStage].next : [stagesobj[currentStage].next])]
      let lengthOfNextStages = nextStages.length
      for (let i = 0; i < lengthOfNextStages; i++) {
        queue.push(nextStages[i])
      }
      visited[currentStage] = true
      finalArr.push(currentStage)
    } else if (visited[currentStage] === false) {
      visited[currentStage] = true
      finalArr.push(currentStage)
    }
    tempQueue = queue
  } while (tempQueue && tempQueue.length)

  if (isJORConversation) {
    let respApp = sortStagesForJor(app)
    let jorStages = respApp.config.stages

    for (let [stage, stageValue] of jorStages) {
      if (visited[stage] === false) {
        finalArr.push(stage)
        visited[stage] = true
      }
    }
  }

  Object.keys(visited).forEach(stage => {
    if (visited[stage] === false) {
      finalArr.push(stage)
    }
  })

  return finalArr
}

/**
 * @function validateMessageSet
 * @description get the list of all missing translation stages
 * @param {object} stages - array of stages
 */
export const validateMessageSet = (stage) => {
  if (stage['text'] && Array.isArray(stage['text']) && stage['text'].length) {
    for (const [index, messageSet] of stage['text'].entries()) {
      for (const country of Object.keys(messageSet)) {
        let countryWiseTranslations = messageSet[country]
        for (const lang of Object.keys(countryWiseTranslations)) {
          let preStr = `Message ${index + 1} --> ${country} (${lang})`
          let btnArray = countryWiseTranslations[lang].match(/(?<=\{\{).*?(?=\}\})/g)
          let optionArray = countryWiseTranslations[lang].match(/(?<=\[\[).*?(?=\]\])/g)
          if (btnArray || optionArray) {
            let messageAfterButtonOrListRemoved = countryWiseTranslations[lang].trim().replace(/{{(.+?)}}|\[\[(.*?)\]\]/g, '')
            if (!messageAfterButtonOrListRemoved) {
              return {
                resp: false,
                error: {
                  type: 'error',
                  html: `<h4><b>Message ${index + 1} --> ${country} (${lang})</b></h4>
                <h5>Please enter some plain text while using Buttons and Lists</h5>`
                }
              }
            }
          }
          if (Array.isArray(btnArray)) {
            for (let str of btnArray) {
              str = str.trim()
              if (!str) {
                return {
                  resp: false,
                  error: {
                    type: 'error',
                    html: `<h4><b>${preStr}</b></h4>
                    <h5>Please Add Button text</h5>`
                  }
                }
              } else if (isButtonTextGreaterThan20Chars(str)) {
                return {
                  resp: false,
                  error: {
                    type: 'error',
                    html: `<h4><b>${preStr}</b></h4>
                    <h5>Button text should be less than 20 characters</h5>`
                  }
                }
              }
            }
          }
          if (Array.isArray(btnArray) && btnArray.length > 10) {
            return {resp: false, error: {
              type: 'error',
              html: `<h4><b>${preStr}</b></h4> 
              <h5>Maximum of 10 options allowed for buttons/lists.</h5>
              <h5>Please use plain text format if more than 10 options are needed.</h5>`
            }}
          }
          if ((Array.isArray(btnArray) && btnArray.length <= 3) && (Array.isArray(optionArray) && optionArray.length)) {
            return {resp: false, error: {
              type: 'error',
              html: `<h4><b>${preStr}</b></h4> 
              <h5>List options not needed for less then 3 buttons</h5>`
            }}
          }          
          if ((Array.isArray(btnArray) && btnArray.length > 3 && !btnArray[0].includes('NINETY')) && (!optionArray || !optionArray.length)) {
            return {resp: false, error: {
              type: 'error',
              html: `<h4><b>${preStr}</b></h4>
              <h5>List options needed for more than 3 buttons.</h5>
              <h5>Please add list option like [[List Button Text]]</h5>`
            }}
          }
          if ((!btnArray || !btnArray.length) && (Array.isArray(optionArray) && optionArray.length)) {
            return {resp: false, error: {
              type: 'error',
              html: `<h4><b>${preStr}</b></h4> 
              <h5>Add list option when more then 3 buttons</h5>`
            }}
          }
          if (Array.isArray(optionArray)) {
            for (let str of optionArray) {
              str = str.trim()
              if (!str) {
                return {
                  resp: false,
                  error: {
                    type: 'error',
                    html: `<h4><b>${preStr}</b></h4>
                    <h5>Please Add list option text</h5>`
                  }
                }
              } else if (isButtonTextGreaterThan20Chars(str)) {
                return {
                  resp: false,
                  error: {
                    type: 'error',
                    html: `<h4><b>${preStr}</b></h4>
                    <h5>List option text should be less than 20 characters</h5>`
                  }
                }
              }
            }
          }
          if (Array.isArray(optionArray) && optionArray.length > 1) {
            return {resp: false, error: {
              type: 'error',
              html: `<h4><b>${preStr}</b></h4> 
              <h5>There should be only one list option</h5>`
            }}
          }
        }
      }
    }
  }

  return {resp: true, error: null}
}

let countryList = []
const getActiveCountries = () => {
  return new Promise(resolve => {
   api.buildRequest('getActivecountryList', 'GET').then(({ data }) => {
    countryList = data
    resolve()
  })})
}

export const getActiveCountryList = async (formatType = 'uppercase', includeGlobal = false) => {  
  if (!countryList.length) await getActiveCountries()

  const arrayToReturn = includeGlobal ? ['Global', ...countryList] : countryList

  if (formatType === 'uppercase') {
    return arrayToReturn.map(item => item)
  } else if (formatType === 'lowercase') {
    return arrayToReturn.map(item => item.toLowerCase())
  } else if (formatType === 'obj') {
    return arrayToReturn.map(item => ({label: item, id: item}))
  }
}

export const isButtonTextGreaterThan20Chars = (buttonText) => {
  // Define the ranges for full letters in Unicode block
  const allScriptFullLetterRanges = [
    { start: 0x0041, end: 0x005A }, // Uppercase A-Z
    { start: 0x0061, end: 0x007A },  // Lowercase a-z
    { start: 0x0020, end: 0x002F }, // Spaces and punctuation
    { start: 0x003A, end: 0x0040 }, // More punctuation
    { start: 0x005B, end: 0x0060 }, // More punctuation
    { start: 0x007B, end: 0x007E },  // More punctuation
    { start: 0x0966, end: 0x096F }, // Devanagari digits ०-९
    { start: 0x0904, end: 0x0939 }, // Devanagari
    { start: 0x0958, end: 0x095F }, // Devanagari extended
    { start: 0x09E6, end: 0x09EF }, // Assamese digits ০-৯
    { start: 0x0985, end: 0x098C }, // Assamese vowels
    { start: 0x098F, end: 0x0990 }, // Assamese vowels continued
    { start: 0x0993, end: 0x09A8 }, // Assamese consonants
    { start: 0x09AA, end: 0x09B0 }, // Assamese consonants continued
    { start: 0x09B2, end: 0x09B2 }, // Assamese L
    { start: 0x09B6, end: 0x09B9 }, // Assamese consonants continued
    { start: 0x09DC, end: 0x09DD }, // Assamese additional consonants
    { start: 0x09DF, end: 0x09E1 }  // Assamese additional consonants continued
  ]

  // Initialize counts
  let allScriptCount = 0;

  // Iterate over each character in the input string and count accordingly
  [...buttonText].forEach(char => {
    const charCode = char.charCodeAt(0)
    if (allScriptFullLetterRanges.some(range => charCode >= range.start && charCode <= range.end)) {
      allScriptCount++
    }
  })
  
  // Return the counts
  return allScriptCount > 20
}


//////////////////
// WEBPACK FOOTER
// ./utils/utilities.js
// module id = JRs0
// module chunks = 1